import { combineReducers } from 'redux';

import { getSortingState, setDefaultValueForFiltersByKey } from './common';
import { adminList } from './patients-admin-list-reducer';
import { SharingStatusFilters } from '@shared/constants/filters';
import { AllActions } from '@shared/redux/actions';
import * as PatientActionTypes from '@shared/redux/actions/patientActions/patient-actions';
import { PatientActions } from '@shared/redux/actions/patientActions/patient-actions';
import * as PatientsTherapyFiltersActions from '@shared/redux/actions/patientActions/patients-therapy-filters-actions';
import {
  ChangePatientsSortingPayload,
  SetActivePatientListTabPayload,
} from '@shared/redux/actions/payloadTypes/patientActionPayloads';
import { ActionWithPayload } from '@shared/redux/actions/typings';
import * as UserActionTypes from '@shared/redux/actions/users-actions';
import { initPatientsRootState } from '@shared/redux/reducers/initState/initPatientsRootState';
import {
  ExpandedPatientRowKeyState,
  FiltersState,
  SearchQueryState,
  SortState,
  TabsState,
} from '@shared/redux/state/patients';
import { PatientListTabIds } from '@shared/types/patientListTypes';

function tabs(state: TabsState = initPatientsRootState.tabs, action: PatientActions): TabsState {
  switch (action.type) {
    case PatientActionTypes.SET_ACTIVE_PATIENT_LIST_TAB: {
      const { tabId }: SetActivePatientListTabPayload = action.payload;
      return { activeTab: tabId };
    }
  }

  return state;
}

function searchQuery(
  state: SearchQueryState = initPatientsRootState.searchQuery,
  action: PatientActions,
): SearchQueryState {
  switch (action.type) {
    case PatientActionTypes.SET_PATIENTS_SEARCH_QUERY: {
      return action.payload.searchQuery;
    }
  }
  return state;
}

function sdbTogglePatientExpansion(
  state: ExpandedPatientRowKeyState = initPatientsRootState.sdbTelemonitoring.expandedPatientKey,
  action: PatientActions,
): ExpandedPatientRowKeyState {
  switch (action.type) {
    case PatientActionTypes.SDB_TOGGLE_PATIENT_EXPANSION: {
      const { patientRowKey } = action.payload;
      return state === patientRowKey ? null : patientRowKey;
    }
  }

  return state;
}

function appliedFilters(state: FiltersState = initPatientsRootState.filters, action: ActionWithPayload): FiltersState {
  switch (action.type) {
    case PatientsTherapyFiltersActions.THERAPY_SET_FILTER:
      return {
        ...state,
        [action.payload.filterKey]: action.payload.filterValue,
      };
    case PatientsTherapyFiltersActions.THERAPY_CHANGE_SHARING_STATUS:
      return {
        ...state,
        sharedByMeToTenant: '',
        sharedToMeFromTenant: '',
        sharingStatusFilter: action.payload.filterValue,
      };
    case PatientsTherapyFiltersActions.THERAPY_CHANGE_SHARED_TO_ME:
      return {
        ...state,
        sharedByMeToTenant: '',
        sharingStatusFilter: SharingStatusFilters.Empty,
        sharedToMeFromTenant: action.payload.filterValue,
      };
    case PatientsTherapyFiltersActions.THERAPY_SHARE_TO_TENANT:
      return {
        ...state,
        sharedToMeFromTenant: '',
        sharingStatusFilter: SharingStatusFilters.Empty,
        sharedByMeToTenant: action.payload.filterValue,
      };
    case PatientsTherapyFiltersActions.THERAPY_SELECT_ALL:
      return {
        ...state,
        [action.payload.filterKey]: action.payload.filterValue,
      };
    case PatientsTherapyFiltersActions.THERAPY_SELECT_NONE:
      return {
        ...state,
        [action.payload.filterKey]: [],
      };
    case PatientsTherapyFiltersActions.THERAPY_SET_FILTERS_STATE:
      return {
        ...state,
        ...action.payload,
      };
    case PatientsTherapyFiltersActions.THERAPY_REMOVE_FILTER:
      return setDefaultValueForFiltersByKey(action.payload.filterKey, state);
    case PatientsTherapyFiltersActions.THERAPY_CLEAR_FILTERS:
      return initPatientsRootState.filters;
    case PatientsTherapyFiltersActions.THERAPY_CHANGE_USERS_IN_CHARGE:
      const filterValue = action.payload.filterValue;
      const valueToSet = state.usersInCharge.includes(filterValue)
        ? state.usersInCharge.filter((user) => user !== filterValue)
        : [...state.usersInCharge, filterValue];

      return {
        ...state,
        usersInCharge: valueToSet,
      };

    case UserActionTypes.USER_DELETE_SUCCESS:
      return {
        ...state,
        usersInCharge: state.usersInCharge.filter((i) => i !== action.payload.userId),
      };
  }
  return state;
}

function sdbChangePatientsSorting(
  state: SortState = initPatientsRootState.sdbTelemonitoring.sort,
  action: ActionWithPayload<ChangePatientsSortingPayload>,
): SortState {
  const { type, payload } = action;

  if (type === PatientActionTypes.SDB_CHANGE_PATIENTS_SORTING) {
    return getSortingState(state, payload);
  }

  return state;
}

function ventilationTogglePatientExpansion(
  state: ExpandedPatientRowKeyState = initPatientsRootState.ventilationTelemonitoring.expandedPatientKey,
  action: PatientActions,
): ExpandedPatientRowKeyState {
  switch (action.type) {
    case PatientActionTypes.VENTILATION_TOGGLE_PATIENT_EXPANSION: {
      const { patientRowKey } = action.payload;
      return state === patientRowKey ? null : patientRowKey;
    }
  }

  return state;
}

function ventilationChangePatientsSorting(
  state: SortState = initPatientsRootState.ventilationTelemonitoring.sort,
  action: ActionWithPayload<ChangePatientsSortingPayload>,
): SortState {
  const { type, payload } = action;

  if (type === PatientActionTypes.VENTILATION_CHANGE_PATIENTS_SORTING) {
    return getSortingState(state, payload);
  }

  return state;
}

function sdbToggleScheduledPatientsExpansion(
  state: boolean = initPatientsRootState.sdbTelemonitoring.scheduledPatientsExpanded,
  action: AllActions,
): boolean {
  switch (action.type) {
    case PatientActionTypes.TOGGLE_SCHEDULED_PATIENTS_EXPANSION: {
      if (action.payload.activeTab === PatientListTabIds.SdbTelemonitoring) {
        return action.payload.expanded;
      }
      return state;
    }

    default:
      return state;
  }
}

function ventilationToggleScheduledPatientsExpansion(
  state: boolean = initPatientsRootState.ventilationTelemonitoring.scheduledPatientsExpanded,
  action: AllActions,
): boolean {
  switch (action.type) {
    case PatientActionTypes.TOGGLE_SCHEDULED_PATIENTS_EXPANSION: {
      if (action.payload.activeTab === PatientListTabIds.VentilationTelemonitoring) {
        return action.payload.expanded;
      }
      return state;
    }

    default:
      return state;
  }
}

const sdbTelemonitoring = combineReducers({
  sort: sdbChangePatientsSorting,
  expandedPatientKey: sdbTogglePatientExpansion,
  scheduledPatientsExpanded: sdbToggleScheduledPatientsExpansion,
});

const ventilationTelemonitoring = combineReducers({
  sort: ventilationChangePatientsSorting,
  expandedPatientKey: ventilationTogglePatientExpansion,
  scheduledPatientsExpanded: ventilationToggleScheduledPatientsExpansion,
});

export const patients = combineReducers({
  tabs,
  searchQuery,
  sdbTelemonitoring,
  ventilationTelemonitoring,
  filters: appliedFilters,
  adminList,
});
